// PARTNER-PORTÁL app-shell — a partner-terv szerint (a MEGÉPÜLT állapot: `docs/foundations/design.md` §9): üveg sidebar (240px) + 60px topbar +
// mobil alsó nav + mobil menü.
//
// MIÉRT KÜLÖN SHELL, ÉS MIÉRT NEM MÁSOLAT: a design-RENDSZER közös a belső felülettel (tokenek, tipográfia,
// komponensek, üveg-geometria — `sidebar-glass m-2 rounded-2xl w-60`, 60px topbar), az INFORMÁCIÓ-
// ARCHITEKTÚRA viszont más: négy menüpont, fiók-váltó (nem app-váltó), segítség-kártya, alsó nav mobilon,
// és NINCS globális kereső / Mo-panel / parancspaletta. A mérés és az érvelés:
// `docs/foundations/design.md` §9.
//
// A FELÜLET-VÁLASZTÁS a `/session` `type`-jából dől el (user-döntés: egy belépő-felület, egy cím).
// Ez a shell BELSŐS tokennel a belső felületre irányít vissza — nem azért, mert az adat kiszivárogna (a fal a
// szerveren van), hanem mert a rossz felületen ragadni értelmezhetetlen állapot.
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalMobileMenu } from '@/components/portal/portal-mobile-menu';
import { PortalMobileNav } from '@/components/portal/portal-mobile-nav';
import { PortalSidebar } from '@/components/portal/portal-sidebar';
import { PortalTopbar } from '@/components/portal/portal-topbar';
import { EmptyState } from '@/components/shared/empty-state';
import { Button } from '@/components/ui/button';
import { ImpersonationBanner } from '@/features/impersonation/components/impersonation-banner';
import { PortalProvider, usePortal } from '@/features/portal/portal-context';
import { usePortalMe } from '@/features/portal/use-portal-me';
import { portalPermissionsOf } from '@/features/portal/use-portal-permissions';
import { usePortalScope } from '@/features/portal/use-portal-scope';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/** A belső (munkatársi) tokennel a portálra tévedt hívót a belső felületre irányítjuk. */
function useInternalRedirect(): void {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    void api.session
      .query({})
      .then((s) => {
        if (!cancelled && s.status === 200 && s.body.type === 'internal') {
          void navigate({ to: '/', replace: true });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [navigate]);
}

export function PortalShell({ children }: { children: ReactNode }): JSX.Element {
  // A FIÓK-SCOPE a keret-fetch FÖLÖTT dől el: ő a `/portal/me` paramétere is (a nav-jelvények rá szűkülnek),
  // tehát nem lehet a providerben — lásd `use-portal-scope.ts`.
  const scope = usePortalScope();
  const meQuery = usePortalMe(scope.accountId);
  const me = meQuery.data?.status === 200 ? meQuery.data.body : undefined;
  useInternalRedirect();

  return (
    <PortalProvider me={me} scope={scope}>
      <PortalFrame noAccess={meQuery.data?.status === 403}>{children}</PortalFrame>
    </PortalProvider>
  );
}

/** A keret maga — a contextet MÁR használhatja (a provideren belül van). */
function PortalFrame({
  children,
  noAccess,
}: {
  children: ReactNode;
  noAccess: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, accountId, setAccountId, head } = usePortal();
  const [menuOpen, setMenuOpen] = useState(false);

  // PORTÁL-HOZZÁFÉRÉS NÉLKÜL nincs mit kerethez adni: se fiók, se nav-cél. A shell ilyenkor a magyarázatot
  // mutatja (kitől kérjen hozzáférést) — üres sidebar + üres lap helyett.
  if (noAccess) {
    return (
      <div className="bg-background text-foreground app-viewport app-safe-area flex items-center justify-center p-6">
        <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
      </div>
    );
  }

  return (
    <div className="app-viewport app-safe-area flex overflow-hidden">
      <PortalSidebar me={me} accountId={accountId} onAccountChange={setAccountId} />

      <PortalMobileMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        me={me}
        accountId={accountId}
        onAccountChange={setAccountId}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <PortalTopbar
          title={head.title}
          subtitle={head.subtitle}
          crumbs={head.crumbs}
          onOpenMenu={() => setMenuOpen(true)}
        />
        {/* MEGSZEMÉLYESÍTÉS-SÁV a PORTÁL-keretben is: ha a támogató egy PARTNER kapcsolattartót személyesít
            meg, ez a felület töltődik be — a sáv nélkül itt sem látszana, hogy nem a saját nevünkben vagyunk.
            A `mx-3 md:mx-4` PONTOSAN a lenti tartalom-sáv paddingja, tehát a sáv ott kezdődik és ott ér
            véget, ahol a belépő lap „Üdvözöljük, …" kártyája (user-döntés). A `main` FÖLÖTT áll, a
            görgetésen kívül — így görgetés közben is látszik, és a mobil alsó nav sem takarja (a korábbi
            `mb-[62px]` kerülő ezzel megszűnt).
            A FELSŐ TÉRKÖZ deszktopon `mt-px`: így a sáv TETEJE egy vonalban van a sidebar FIÓK-VÁLTÓJÁNAK
            tetejével (user-kérés, 2026-09-09). Mérve 1440×900-on: a topbar alja 60, a márka-blokk alja — - a
            váltó teteje — 61 (az 1px a sidebar üveg-szegélye), tehát a sáv 60 + 1 = 61-en indul. Mobilon
            marad a `mt-4`: ott nincs sidebar, amihez igazodni kellene, és a sáv nem ülhet a topbarra. */}
        <ImpersonationBanner className="mx-3 mt-4 md:mx-4 md:mt-px" />
        {/* Csak a TARTALOM görög (a topbar fix) — a belső shell mintája. A mobil alsó nav 62px + biztonsági
            sáv miatt kap alsó térközt, hogy az utolsó sor ne kerüljön a nav alá. */}
        {/* NINCS `scrollbar-gutter` a `main`-en. Volt (`stable both-edges`), hogy a görgetősáv ne csak a
            jobb oldalon vegyen el helyet — de az a BAL oldalon is lefoglalt 10px-et, és ezzel a lap-tartalom
            10px-cel BELJEBB kezdődött, mint a topbar címe (mérve 1440-en: cím 272 · tábla 282 — user-lelet,
            2026-09-09). A címhez való igazodás erősebb szempont, és a `fill`-lapokon a kérdés eleve
            megszűnik: ott a `main` NEM görget (a görgetés a lapon belül van), tehát görgetősáv sincs. */}
        {/* `scrollbar-none`: a keret görgetője helyet NEM foglaló sávval megy. Nélküle a klasszikus
            görgetősáv 10px-et vett el a tartalom-dobozból, tehát a FOLYÓ lapok (kezdőlap, jegy-részlet,
            üzenetek) jobb széle 10px-cel beljebb került, mint a topbar jobb széle — a lista-lapon (`fill`,
            nincs keret-görgetés) viszont pontosan illeszkedett. Ez volt a látható elcsúszás (user-lelet,
            2026-09-09). A `fill`-lapokon a tábla SAJÁT görgetősávja megmarad, az a kártyán belül van. */}
        <main className="scrollbar-none app-safe-bottom min-h-0 flex-1 overflow-y-auto">
          {/* BAL-IGAZÍTOTT tartalom-sáv (user-döntés 2026-09-02): a belső felület sem központoz — ott a
              lap-tartalom `px-4`-rel a bal széltől indul. A terv `page-body`-ja `max-width:1440px; margin:0 auto`
              volt, de a KÉT FELÜLET EGYSÉGE erősebb szempont: a partner ugyanabban a rendszerben mozog, és
              1920-on a középre húzott sáv „másik alkalmazásnak" látszott. */}
          {/* A VÍZSZINTES térköz `px-4` — PONTOSAN a topbar `px-4`-je, tehát a lap-tartalom bal széle a
              cím bal szélével egy vonalban van (a belső felület is így áll). A `fill` mód a BELSŐ
              lista-oldalak geometriáját veszi át (`flex h-full flex-col px-4 pb-2`): a lap kitölti a
              magasságot, a kártya alja `pb-2`-vel áll meg — egy vonalban a sidebar aljával. */}
          <div
            className={cn(
              // A FELSŐ térköz mindkét módban ugyanaz (a portálon a fül-sáv/eszköztár a LAPON van, nem a
              // keret sticky-sávjában). Az ALSÓ: `fill`-ben `pb-2` = a sidebar alsó térköze; egyébként
              // nagyobb köz, hogy az utolsó blokk ne érjen a viewport széléhez. Mobilon MINDKÉT módban
              // kell a 86px, mert az alsó navigáció (62px + biztonsági sáv) különben eltakarná az alját.
              'w-full px-4 pt-4 md:pt-5',
              head.fill ? 'flex h-full min-h-0 flex-col pb-[86px] md:pb-2' : 'pb-[86px] md:pb-8',
            )}
          >
            {children}
          </div>
        </main>
      </div>

      <PortalMobileNav me={me} />

      {/* MOBIL FAB — CSAK ott, ahol a lap kéri (`head.newTicketFab`), ma egyedül a jegy-listán. A partner a
          portálon egyetlen dolgot kezdeményezhet (jegyet nyit), és a gomb korábban MINDEN lapon ott volt:
          a jegy-nyitón, a kezdőlapon (ahol dedikált „Új jegy" gomb áll), a számláknál, az üzeneteknél és a
          profilon is (user-lelet, 2026-09-09). A döntés a LAPÉ, nem a keretnek: lásd a `PortalHead`
          `newTicketFab` doc-blokkját.
          A MÉRET a belső felület FAB-jával egyezik (`MobileActionFab`): 36px gomb, 20px ikon — a korábbi
          52/24 mellette „másik alkalmazásnak" látszott (user-lelet, 2026-09-08). A POZÍCIÓ szándékosan eltér:
          a portálon alsó navigáció is van, ezért a gomb 76px-re ül a képernyő aljától (a belső FAB-nak nincs
          mit kikerülnie, ott `bottom-4`). */}
      {head.newTicketFab === true &&
        portalPermissionsOf(me).has(PORTAL_PERMISSIONS.ticketsWrite) && (
          <Button
            variant="brand"
            size="icon"
            aria-label={t('portal.newTicket.action')}
            onClick={() => void navigate({ to: '/portal/tickets/new' })}
            className="fixed bottom-[76px] right-4 z-30 size-9 rounded-full shadow-lg md:hidden [&_svg]:size-5"
          >
            <PlusIcon />
          </Button>
        )}
    </div>
  );
}
